// Root mount point for the entire static application.
const appEl = document.getElementById('app');

// Provisional SysEx contract for the companion firmware developer.
// All payload bytes are kept 7-bit clean so they can be sent safely
// through standard MIDI SysEx transports without extra escaping logic.
const SYSEX_SPEC = {
  manufacturerId: [0x00, 0x21, 0x7F],
  productId: 0x03,
  command: {
    resetOptions: 0x01,
    writeOptions: 0x02,
    resetCalibration: 0x03,
    writeCalibration: 0x04,
  },
};

// Select options for the board revision dropdown.
const BOARD_REVISIONS = [
  { value: 'a', label: 'A' },
  { value: 'b', label: 'B' },
];

// Select options for the clock type dropdown.
const CLOCK_TYPES = [
  { value: 'zx', label: 'ZX' },
  { value: 'atari', label: 'Atari' },
  { value: 'legacy', label: 'Legacy' },
];

const CLOCK_TYPES_A = [
  { value: 'atari', label: 'Atari' },
  { value: 'legacy', label: 'Legacy' },
];

// Select options for the envelope timing mode dropdown.
const ENV_PERIOD_TYPES = [
  { value: 'log-linear', label: 'Log / Linear' },
  { value: 'legacy-lut', label: 'Legacy lookup table (LUT)' },
];

// Default editor values shown on first load or when the user hits reset.
const DEFAULT_SETTINGS_B = {
  boardRevision: 'b',
  masterChannel: 1,
  envPeriodType: 'log-linear',
  clockType: 'zx',
};

const DEFAULT_SETTINGS_A = {
  boardRevision: 'a',
  masterChannel: 1,
  envPeriodType: 'log-linear',
  clockType: 'atari',
};


// Clock-specific calibration defaults.
// Each clock keeps its own calibration profile so switching clocks can
// update the visible controls without losing the other profiles.
const DEFAULT_CALIBRATION = {
  zx: {
    chip1Offset: 2460,
    chip2Offset: 90,
    maxOffset: 2478,
    pwmCycleOffset: 7,
    maxCycleOffset: 8,
    enabled: true,
  },
  atari: {
    chip1Offset: 2478,
    chip2Offset: 42,
    maxOffset: 2499,
    pwmCycleOffset: 1,
    maxCycleOffset: 3,
    enabled: true,
  },
  legacy: {
    chip1Offset: 2499,
    chip2Offset: 0,
    maxOffset: 2499,
    pwmCycleOffset: 0,
    maxCycleOffset: 3,
    enabled: false,
  },
};

// Enum lookup table used when building compact SysEx payload bytes.
const ENUM_MAP = {
  boardRevision: {
    a: 0,
    b: 1,
  },
  clockType: {
    atari: 0,
    zx: 1,
    legacy: 2,
  },
  envPeriodType: {
    'log-linear': 0,
    'legacy-lut': 1,
  },
};

// Central app state.
// This is a small enough app that a plain object plus a full re-render
// keeps the code easier to follow than a framework.
const state = {
  midiAccess: null,
  midiOutputId: '',
  settings: { ...DEFAULT_SETTINGS_B },
  calibration: cloneCalibrationProfiles(),
  midiRequestPending: false,
  midiAutoRequested: false,
  notice: {
    tone: 'info',
    message: 'Checking WebMIDI support...',
  },
};

// Rebuild the interface from the current state.
// Because the UI is generated from one place, the visible controls,
// previews, and WebMIDI feedback stay consistent after every action.
function render() {
  const outputs = getOutputs();
  const activeClock = getActiveClockConfig();
  const noticeToneClass = `notice-${escapeHtml(state.notice.tone)}`;
  const noticeMarkup = state.notice.message
    ? `
        <div class="transport-status topbar-status ${noticeToneClass}">
          ${escapeHtml(state.notice.message)}
        </div>
      `
    : '';
  const showRetryButton =
    'requestMIDIAccess' in navigator &&
    !state.midiAccess &&
    !state.midiRequestPending &&
    state.midiAutoRequested;
  const midiActionMarkup = showRetryButton
    ? '<button class="button button-secondary" data-action="enable-midi">Retry WebMIDI</button>'
    : '';

  appEl.innerHTML = `
    <div class="shell">
      <div class="backdrop"></div>
      <main class="app">
        <section class="topbar panel">
          <div class="topbar-brand">
            <p class="eyebrow">AY3 TOOL V1.0</p>
            <div class="topbar-brand-row">
              <img class="hero-logo" src="tool-ay3-logo.png" alt="AY3 logo" />
              <p class="topbar-note">Requires firmware 4.4 or later</p>
            </div>
          </div>
          <div class="topbar-controls">
            ${midiActionMarkup}
            <label class="field topbar-output">
              <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Output Port</span>
              <select id="midi-output">
                ${
                  outputs.length
                    ? outputs
                        .map(
                          (output) => `
                            <option value="${escapeHtml(output.id)}" ${
                              output.id === state.midiOutputId ? 'selected' : ''
                            }>
                              ${escapeHtml(formatOutputName(output))}
                            </option>
                          `
                        )
                        .join('')
                    : '<option value="">No MIDI outputs detected</option>'
                }
              </select>
            </label>
            <button class="button button-secondary" data-action="refresh-outputs">Refresh</button>
            ${noticeMarkup}
          </div>
        </section>

        <section class="content-grid">
          <section class="panel section-card">
            <div class="section-head">
              <div>
                <p class="section-label">Options</p>
                <h2>Device setup</h2>
              </div>
              <div class="button-row">
                <button class="button button-ghost" data-action="reset-options">Reset</button>
                <button class="button" data-action="write-options">Write</button>
              </div>
            </div>
            <div class="fields-grid">
              <label class="field">
                <span>Board Revision</span>
                <select data-setting="boardRevision">
                  ${BOARD_REVISIONS.map(renderOption('boardRevision', state.settings.boardRevision)).join('')}
                </select>
              </label>
              <label class="field">
                <span>Clock Type</span>
                <select data-setting="clockType">
                  ${
                    // fix the unavailable clocktype for rev A
                    state.settings.boardRevision === 'a'
                      ? CLOCK_TYPES_A.map(renderOption('clockType', state.settings.clockType)).join('')
                      : CLOCK_TYPES.map(renderOption('clockType', state.settings.clockType)).join('')
                  }
                </select>
              </label>
              <label class="field">
                <span>Master Channel</span>
                <select data-setting="masterChannel">
                  ${Array.from({ length: 16 }, (_, index) => {
                    const value = index + 1;
                    return `<option value="${value}" ${value === state.settings.masterChannel ? 'selected' : ''}>${value}</option>`;
                  }).join('')}
                </select>
              </label>
              <label class="field">
                <span>Envelope Period Type</span>
                <select data-setting="envPeriodType">
                  ${ENV_PERIOD_TYPES.map(renderOption('envPeriodType', state.settings.envPeriodType)).join('')}
                </select>
              </label>
            </div>
          </section>

          <section class="panel section-card">
            <div class="section-head">
              <div>
                <p class="section-label">Calibration</p>
                <h2>${activeClock.label} clock profile</h2>
              </div>
              <div class="button-row">
                <button class="button button-ghost" data-action="reset-calibration">Reset</button>
                <button class="button" data-action="write-calibration" ${activeClock.enabled ? '' : 'disabled'}>Write</button>
              </div>
            </div>
            ${
              activeClock.enabled
                ? `
                  <div class="calibration-grid">
                    ${renderCalibrationControl({
                      key: 'chip1Offset',
                      label: 'Update-Timer Offset:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Chip 1 (<i>Left</i>)',
                      value: activeClock.chip1Offset,
                      max: activeClock.maxOffset,
                      defaultValue: DEFAULT_CALIBRATION[state.settings.clockType].chip1Offset,
                    })}
                    ${renderCalibrationControl({
                      key: 'chip2Offset',
                      label: 'Update-Timer Offset:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Chip 2 (<i>Right</i>)',
                      value: activeClock.chip2Offset,
                      max: activeClock.maxOffset,
                      defaultValue: DEFAULT_CALIBRATION[state.settings.clockType].chip2Offset,
                    })}
                    ${renderCalibrationControl({
                      key: 'pwmCycleOffset',
                      label: 'PWM-Clock Cycle Offset',
                      value: activeClock.pwmCycleOffset,
                      max: activeClock.maxCycleOffset,
                      defaultValue: DEFAULT_CALIBRATION[state.settings.clockType].pwmCycleOffset,
                    })}
                  </div>
                  <div class="calibration-meta">
                    <span>Update-Timer Cycle Range <i>~50hz (0..180° phase shift)</i>: [0 .. ${activeClock.maxOffset}]</span>
                    <span>PWM-Clock Cycle Range <i>1.77/2Mhz</i>: [0 .. ${activeClock.maxCycleOffset}]</span>
                  </div>
                `
                : `
                  <div class="empty-state">
                    <strong>Legacy clock disables calibration.</strong>
                    <p>Switch to ZX or Atari to edit the timer offsets and PWM cycle offset.</p>
                  </div>
                `
            }
            <p class="callout2">
              <i><u style="text-decoration: underline dotted #57a3ef; text-underline-offset: 4px;">Be careful with calibration:</u></i><br>
              <span style="font-size: smaller;">By design, the timers are synchronised with the clock and with each other, but units vary from 
              the factory, and a value that works on one AY3 may not be stable on another. Please note that calibration does not take place by 
              approximation (miscalibration can be caused simply by changing one step), but depends on a specific interval between the three 
              parameters below. When scanning (playing & looping a reference song), try to place the two chip timer offsets as far apart as 
              possible, as otherwise this will impair the stereo effect. Decalibration will result in crackling and a distorted sound, which is 
              most noticeable in the pitch shift of the voice, but you can always reset it to its factory settings here or by pressing 
              <b>VOICE + d</b> during boot-up.</span>
            </p>
            <p class="callout">
              <i><u style="text-decoration: underline dotted #57a3ef; text-underline-offset: 4px;">How to calibrate:</u></i><br>
              <span style="font-size: smaller;">The goal is to minimize PWM timer-induced noise while maintaining the stereo image. To achieve 
              this, both update timers must be set to their maximum spread, but they must also match the peaks of the PWM timer. One could 
              first align the left update timer with the PWM timer values and collect acceptable values, and then do the same for the right 
              one, until both have minimized their respective noise individually or the timers 'sound' in sync. Then both update timers must be 
              compared with each other so that any remaining noise either overlaps as much as possible or becomes negligible. Note that creaking 
              noises are normal for the older rev A boards with 8912 chips, especially at high frequencies, and that these boards generally 
              synchronize much less accurately. In contrast, for rev B boards, an acceptable value for the update timer was measured every 9. step. 
              If an acceptable pair is ultimately found for a specific PWM cycle, it can be saved as the final calibration setup. If everything 
              sounds wrong, hit reset and try again. However, the boards (especially the newer) generally do not need to be tuned if the 
              behavior is due to the processor itself and not to variations between models of the same chip. The tool simply provides a way to 
              make these adjustments. Since values can only be written and not read, you should make a note of the appropriate values or, better yet, 
              share them with twisted electrons.</span>
            </p>
          </section>
        </section>
      </main>
    </div>
  `;

  bindEvents();
}

// Attach event listeners after every render.
// Since the DOM is rebuilt from scratch, listeners are rebound here.
function bindEvents() {
  appEl.querySelector('[data-action="enable-midi"]')?.addEventListener('click', enableMidi);
  appEl.querySelector('[data-action="refresh-outputs"]')?.addEventListener('click', refreshOutputs);
  appEl.querySelector('[data-action="write-options"]')?.addEventListener('click', () => sendPacket('options'));
  appEl.querySelector('[data-action="write-calibration"]')?.addEventListener('click', () => sendPacket('calibration'));
  appEl.querySelector('[data-action="reset-options"]')?.addEventListener('click', resetOptions);
  appEl.querySelector('[data-action="reset-calibration"]')?.addEventListener('click', resetCalibration);

  appEl.querySelector('#midi-output')?.addEventListener('change', (event) => {
    state.midiOutputId = event.target.value;
    const output = getSelectedOutput();
    if (output) {
      setNotice('success', `Selected MIDI output: ${formatOutputName(output)}`);
    }
    render();
  });

  appEl.querySelectorAll('[data-setting]').forEach((input) => {
    input.addEventListener('change', (event) => {
      const key = event.target.dataset.setting;
      const rawValue = event.target.value;
      state.settings[key] = key === 'masterChannel' ? Number(rawValue) : rawValue;
      render();
    });
  });

  // Range sliders update in real time while dragging.
  appEl.querySelectorAll('[data-calibration]').forEach((input) => {
    //removed because it interferes with dragging:
    //input.addEventListener('input', (event) => {
    //  updateCalibrationValue(event.target.dataset.calibration, Number(event.target.value));
    //});
    input.addEventListener('change', (event) => {
      updateCalibrationValue(event.target.dataset.calibration, Number(event.target.value));
    });
  });

  appEl.querySelectorAll('[data-calibration-number]').forEach((input) => {
    input.addEventListener('change', (event) => {
      updateCalibrationValue(event.target.dataset.calibrationNumber, Number(event.target.value));
    });
  });

}

// Returns a renderer function for option lists so the selected value can
// be injected consistently across several dropdowns.
function renderOption(_key, selectedValue) {
  return (option) => `
    <option value="${option.value}" ${option.value === selectedValue ? 'selected' : ''}>
      ${option.label}
    </option>
  `;
}

// Render one calibration control block containing the numeric field and slider.
function renderCalibrationControl({ key, label, value, max, defaultValue }) {
  return `
    <label class="calibration-card">
      <div class="calibration-head">
        <span>${label}</span>
        <input
          class="number-input"
          type="number"
          min="0"
          max="${max}"
          value="${value}"
          data-calibration-number="${key}"
        />
      </div>
      <input
        class="slider"
        type="range"
        min="0"
        max="${max}"
        step="1"
        value="${value}"
        data-calibration="${key}"
      />
      <div class="calibration-scale">
        <span>0</span>
        <span>Default ${defaultValue}</span>
        <span>Max ${max}</span>
      </div>
    </label>
  `;
}

// Convert the MIDI output map into a plain array that is easier to render.
function getOutputs() {
  if (!state.midiAccess) return [];
  return Array.from(state.midiAccess.outputs.values());
}

// Return the active MIDI output selected in the dropdown.
function getSelectedOutput() {
  return getOutputs().find((output) => output.id === state.midiOutputId) ?? null;
}

// Combine the selected clock's calibration values with a human-readable label
// so the render function can treat it as one display object.
function getActiveClockConfig() {
  // fix the unavailable clocktype for rev A
  state.settings.clockType = (state.settings.clockType == 'zx' && state.settings.boardRevision == 'a') ? 'atari' : state.settings.clockType;

  const { clockType } = state.settings;
  const config = state.calibration[clockType];
  const label = CLOCK_TYPES.find((item) => item.value === clockType)?.label ?? clockType;
  return {
    ...config,
    label,
  };
}

// Update the currently visible calibration profile.
// Numeric inputs and range sliders both end up here so clamping logic lives in one place.
function updateCalibrationValue(key, value) {
  const profile = state.calibration[state.settings.clockType];
  if (!profile.enabled) return;

  const max = key === 'pwmCycleOffset' ? profile.maxCycleOffset : profile.maxOffset;
  const safeValue = clamp(Number.isFinite(value) ? value : 0, 0, max);
  state.calibration[state.settings.clockType][key] = safeValue;
  render();
}

// Reset only the option values, leaving all clock-specific calibration profiles intact.
function resetOptions() {
  state.settings = state.settings.boardRevision == 'a' ? { ...DEFAULT_SETTINGS_A } : { ...DEFAULT_SETTINGS_B };
  setNotice('info', 'Options reset to defaults.');

  //render();
  sendPacket('resetOptions');
}

// Reset only the calibration profile for the currently selected clock type.
function resetCalibration() {
  const clockType = state.settings.clockType;
  state.calibration[clockType] = { ...DEFAULT_CALIBRATION[clockType] };
  setNotice('info', `${CLOCK_TYPES.find((item) => item.value === clockType)?.label ?? clockType} calibration reset.`);
  
  //render();
  sendPacket('resetCalibration');
}

// Ask the browser for WebMIDI access with SysEx enabled.
// A secure context is required by the browser, so this should be served
// from localhost or HTTPS rather than opened as file://.
async function enableMidi() {
  if (!('requestMIDIAccess' in navigator)) {
    setNotice('error', 'WebMIDI is not supported in this browser. Use Chrome or Edge on desktop.');
    render();
    return;
  }

  if (state.midiRequestPending) {
    return;
  }

  state.midiRequestPending = true;
  state.midiAutoRequested = true;

  try {
    setNotice('info', 'Requesting WebMIDI access...');
    render();

    state.midiAccess = await navigator.requestMIDIAccess({ sysex: true });
    state.midiAccess.onstatechange = () => {
      syncSelectedOutput();
      render();
    };
    syncSelectedOutput();
    clearNotice();
  } catch (error) {
    const message = error?.message || 'The browser blocked the MIDI request.';
    setNotice('error', `WebMIDI access failed: ${message}`);
  } finally {
    state.midiRequestPending = false;
  }

  render();
}

// Refresh the visible output list after devices are plugged in or removed.
function refreshOutputs() {
  if (!state.midiAccess) {
    setNotice('error', 'WebMIDI is not connected yet. Use Retry WebMIDI if access was denied.');
    render();
    return;
  }

  syncSelectedOutput();
  setNotice('success', 'MIDI outputs refreshed.');
  render();
}

// Keep the selected output valid when the available MIDI outputs change.
function syncSelectedOutput() {
  const outputs = getOutputs();
  if (!outputs.length) {
    state.midiOutputId = '';
    return;
  }

  const selectedExists = outputs.some((output) => output.id === state.midiOutputId);
  if (!selectedExists) {
    state.midiOutputId = outputs[0].id;
  }
}

// Send either the options packet or the current clock's calibration packet.
function sendPacket(kind) {
  const output = getSelectedOutput();
  if (!output) {
    setNotice('error', 'Select a MIDI output before sending SysEx.');
    render();
    return;
  }

  if (!state.midiAccess?.sysexEnabled) {
    setNotice('error', 'WebMIDI access does not have SysEx permission.');
    render();
    return;
  }

  var packet;

  switch (kind) {
    case 'options':           packet = buildOptionsPacket();                                  break;
    case 'calibration':       packet = buildCalibrationPacket(state.settings.clockType);      break;
    case 'resetOptions':      packet = buildResetOptionsPacket();                             break;
    case 'resetCalibration':  packet = buildResetCalibrationPacket(state.settings.clockType); break;
  }

  try {
    output.send(packet);
    setNotice('success', `${kind === 'options' ? 'Options' : 'Calibration'} SysEx sent to ${formatOutputName(output)}.`);
  } catch (error) {
    setNotice('error', `Failed to send SysEx: ${error.message}`);
  }

  render();
}

// Build the compact packet used to transmit the option block.
function buildOptionsPacket() {
  const payload = [
    clamp(state.settings.masterChannel, 1, 16),
    ENUM_MAP.boardRevision[state.settings.boardRevision],
    ENUM_MAP.clockType[state.settings.clockType],
    ENUM_MAP.envPeriodType[state.settings.envPeriodType],
  ];

  return buildPacket(SYSEX_SPEC.command.writeOptions, payload);
}

// Build the packet used to transmit the calibration profile for a specific clock.
function buildCalibrationPacket(clockType) {
  const calibration = state.calibration[clockType];
  return buildPacket(SYSEX_SPEC.command.writeCalibration, [
    ENUM_MAP.clockType[clockType],
    ...encodeUint14(calibration.chip1Offset),
    ...encodeUint14(calibration.chip2Offset),
    calibration.pwmCycleOffset & 0x7f,
  ]);
}

// Build the compact packet used to transmit the option reset.
function buildResetOptionsPacket() {
  return buildPacket(SYSEX_SPEC.command.resetOptions, [
    ENUM_MAP.boardRevision[state.settings.boardRevision],
  ]);
}

// Build the packet used to transmit the calibration reset for a specific clock.
function buildResetCalibrationPacket(clockType) {
  return buildPacket(SYSEX_SPEC.command.resetCalibration, [
    ENUM_MAP.clockType[clockType],
  ]);
}

// Assemble the final SysEx frame.
// Layout:
// F0, manufacturer..., product, version, command, payload..., checksum, F7
function buildPacket(command, payload) {
  const body = [
    ...SYSEX_SPEC.manufacturerId,
    SYSEX_SPEC.productId,
    command & 0x7f,
    ...payload.map((value) => value & 0x7f),
  ];

  // checksum not required!
  // const checksum = body.reduce((sum, byte) => (sum + byte) & 0x7f, 0);
  // return [0xf0, ...body, checksum, 0xf7];

  return [0xf0, ...body, 0xf7];
}

// Split values larger than 127 into low-7/high-7 bytes.
// This keeps the payload valid inside a regular MIDI SysEx stream.
function encodeUint14(value) {
  const safeValue = clamp(value, 0, 0x3fff);
  return [safeValue & 0x7f, (safeValue >> 7) & 0x7f];
}

// Produce a stable display name even if the MIDI device omits one field.
function formatOutputName(output) {
  //return [output.manufacturer, output.name].filter(Boolean).join(' / ') || output.id;
  return output.name || output.id;
}

// Store the latest inline notice shown in the transport card.
function setNotice(tone, message) {
  state.notice = { tone, message };
}

// Remove the inline notice entirely so the toolbar can collapse back down.
function clearNotice() {
  state.notice = { tone: 'info', message: '' };
}

// Create a detached copy of the calibration defaults so live edits do not
// mutate the shared constant object.
function cloneCalibrationProfiles() {
  return JSON.parse(JSON.stringify(DEFAULT_CALIBRATION));
}

// Generic numeric clamp helper used by the calibration controls.
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// Escape user-visible strings before injecting them into template literals.
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

// Request MIDI access automatically on startup so the user does not have
// to click an extra enable button in browsers that support WebMIDI.
function autoEnableMidiIfSupported() {
  if (!('requestMIDIAccess' in navigator)) {
    setNotice('error', 'WebMIDI is not supported in this browser. Use Chrome or Edge on desktop.');
    render();
    return;
  }

  if (state.midiAutoRequested || state.midiAccess) {
    return;
  }

  enableMidi();
}

// First paint.
render();
autoEnableMidiIfSupported();
